import type { RoomView, VoiceToken } from './social.js';

/*
 * WHICH ROOM A WALK-IN LANDED IN — one answer, for both clients.
 *
 * It began on the phone, as a pure module with a `node:test` beside it, because that is where the
 * defect below lost a reader. **The web then answered the same question inline** — the same id
 * out of the same token, the same look-up in the same list, the same stand-in with the same eight
 * fields — and the two agreed only because one was written while the other was open.
 *
 * *A rule copied into two clients is a rule that will differ in one of them* is written beside
 * `mustBeAsked` and is why `licence-policy.ts` exists. So it is here, where both can reach it, and
 * the tests moved with it.
 *
 * WHAT WENT WRONG. The screen minted a token, read the node's room list, looked the room up in it,
 * and when it was not there set the joined room to null and went back to idle: no connection, no
 * message, a live token discarded. And the list omits exactly the room this matters for — one that
 * is live, held open for a scheduled study's hour, and still EMPTY past its two-minute grace is
 * deliberately unlisted (`GROWTH.md §4`). So the first person walking in on a phone got silence,
 * and they were the very person whose arrival would have made the room visible to everyone behind
 * them.
 *
 * The invariant this function exists to hold: **a token in hand always yields a room to be in.**
 * The list if it has caught up, a stand-in if it has not — and `needsRead` says which, so the
 * caller can ask the server for the real thing WITHOUT that read standing between a reader and the
 * conversation.
 */
export interface WalkedInto {
  /** Never null. A reader holding a token is in a room, whatever the list has managed to say. */
  room: RoomView;
  /** True when `room` is the stand-in, so the caller should read the real one and replace it. */
  needsRead: boolean;
}

/** The room id inside a provider room name (`vv:<id>`). */
export function roomIdOf(token: VoiceToken): string {
  return token.room.replace(/^vv:/, '');
}

export function walkedInto(
  rooms: RoomView[],
  token: VoiceToken,
  nodeId: string,
  meId: string | null,
  now: Date,
): WalkedInto {
  const id = roomIdOf(token);
  const known = rooms.find(room => room.id === id);
  if (known) return { room: known, needsRead: false };

  return {
    /*
     * A walk-in is always a commons room — the one-click path is on the node, never under a Hall —
     * so the hall, the access and the node are facts rather than guesses. `policy` and `stageLimit`
     * are the guessed part, which is why `needsRead` exists: a walk-in that joined an EXISTING
     * conversation could be in a stage room, and a wrong policy draws the wrong controls.
     */
    room: {
      id,
      nodeId,
      title: null,
      hallId: null,
      access: 'public',
      hostIds: meId === null ? [] : [meId],
      speakerIds: [],
      /* Empty, like the stage: nobody can have asked to speak in a room this reader just opened,
         and `needsRead` is what corrects a walk-in that joined an existing conversation. */
      raisedIds: [],
      /* A walk-in is a commons room, so there is no Hall to co-host it with — and `needsRead`
         corrects a walk-in that joined a conversation already going. */
      coHall: null,
      policy: 'open',
      stageLimit: 8,
      capacity: 100,
      video: false,
      status: 'live',
      occupancy: 1,
      createdAt: now.toISOString(),
    },
    needsRead: true,
  };
}
